import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { energyService } from '../../services/energyService';
import { logger } from '../../utils/logger';

// ─────────────────────────────────────────────────────────────────────────
// EnergyRangePicker — the window every Level (1/2/3) is read through.
//
// SALARY CYCLES, not fixed boxes (#311). This used to offer "7d" and "30d"
// presets, which is the wrong unit for this product: energy is deposited,
// spent and rolled over on the user's purchase-anniversary window, so a 30-day
// box straddles two cycles and a total taken over it reconciles against
// nothing. The pager browses those real cycles instead; "Custom" survives for
// the genuinely arbitrary question.
//
// The anniversary arithmetic — day-clamped, short-month aware — lives in ONE
// place, `app/utils/plans`. This component never re-derives a boundary: it
// pages through the cycles `GET /api/energy/cycles` hands it. A second
// implementation in JavaScript would be an off-by-one away from disagreeing
// with the tank the user is reading the numbers against.
//
// The CURRENT cycle still sends NO date params at all, so the default request is
// byte-identical to before and the server remains the authority on "now".
// ─────────────────────────────────────────────────────────────────────────

export const CYCLE_RANGE = { preset: 'cycle', startDate: undefined, endDate: undefined };

/** The range for one entry of the /cycles payload. */
export function rangeForCycle(cycle) {
    if (!cycle || cycle.is_current) return { ...CYCLE_RANGE, cycle };
    return {
        preset: 'cycle',
        startDate: cycle.start,
        // `last_day` is the INCLUSIVE final day. The statement endpoints treat
        // end_date as inclusive, while `end` is the exclusive next-cycle
        // boundary used for the label — sending the wrong one leaks a day of the
        // next cycle into this one's total.
        endDate: cycle.last_day,
        cycle,
    };
}

const fmtDay = (iso) => String(iso || '').slice(0, 10);

const EnergyRangePicker = ({ value = CYCLE_RANGE, onChange }) => {
    const { t } = useLanguage();
    const preset = value?.preset || 'cycle';

    const [cycles, setCycles] = useState(null);
    const alive = useRef(true);

    useEffect(() => {
        alive.current = true;
        energyService
            .getCycles()
            .then((res) => { if (alive.current) setCycles(res?.cycles || []); })
            .catch((err) => {
                // Fail-soft: without the cycle list the pager simply cannot page,
                // and the view stays on the current cycle — which is the default
                // and is served with no date params at all.
                logger.warn('ENERGY', `cycles load failed: ${err?.message || err}`);
                if (alive.current) setCycles([]);
            });
        return () => { alive.current = false; };
    }, []);

    // Where we are in the list. index 0 is the current cycle; larger is older.
    const index = cycles
        ? Math.max(0, cycles.findIndex((c) => (
            preset === 'cycle'
            && (value?.startDate ? c.start === value.startDate : c.is_current)
        )))
        : 0;
    const active = cycles?.[index] || null;

    const goto = (next) => {
        if (!cycles || next < 0 || next >= cycles.length) return;
        onChange?.(rangeForCycle(cycles[next]));
    };

    const isCycle = preset === 'cycle';
    // "Older" is a HIGHER index — the list is newest-first.
    const canOlder = isCycle && cycles != null && index + 1 < cycles.length;
    const canNewer = isCycle && cycles != null && index > 0;

    const label = active
        ? `${fmtDay(active.start)} → ${fmtDay(active.end)}`
        : t('energyRangeCycle', 'This cycle');

    const setBound = (field, v) => onChange?.({ ...value, preset: 'custom', [field]: v || undefined });

    const navButton = (testId, onClick, disabled, Icon, aria) => (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            disabled={disabled}
            aria-label={aria}
            title={aria}
            className="pai-tap flex items-center justify-center w-7 h-7 rounded-seg text-pai-secondary hover:bg-white/[0.1] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent flex-shrink-0"
        >
            <Icon size={14} />
        </button>
    );

    return (
        <div
            data-testid="energy-range"
            data-preset={preset}
            className="flex flex-wrap items-center gap-1.5 min-w-0 max-w-full"
        >
            <div
                data-testid="energy-cycle-selector"
                data-index={isCycle ? index : ''}
                data-current={active?.is_current ? 'true' : 'false'}
                // The design's segmented control: a hairline tray holding the
                // pager, with the selected segment lifted by a white wash rather
                // than a filled accent.
                className="flex items-center gap-1 p-1 rounded-ctl border border-pai-subtle bg-white/[0.03] min-w-0"
            >
                {/* ‹ walks BACKWARDS through history — the list is newest-first,
                    so "previous cycle" is the NEXT index. */}
                {navButton('energy-cycle-prev', () => goto(index + 1), !canOlder, ChevronLeft,
                    t('energyCyclePrev', 'Previous cycle'))}
                <button
                    type="button"
                    data-testid="energy-cycle-label"
                    onClick={() => goto(0)}
                    title={t('energyRangeCycle', 'This cycle')}
                    className={`pai-tap-row px-3 py-1.5 text-[13.5px] font-semibold rounded-seg transition-colors min-w-0 max-w-full truncate tnum ${
                        isCycle
                            ? 'bg-white/[0.1] text-white'
                            : 'text-pai-meta hover:text-white hover:bg-white/[0.06]'
                    }`}
                >
                    {label}
                </button>
                {navButton('energy-cycle-next', () => goto(index - 1), !canNewer, ChevronRight,
                    t('energyCycleNext', 'Next cycle'))}
            </div>

            <button
                type="button"
                data-testid="energy-range-custom"
                aria-pressed={preset === 'custom'}
                onClick={() => onChange?.(
                    preset === 'custom'
                        ? rangeForCycle(active)
                        : {
                            preset: 'custom',
                            // Seeded from the cycle on screen so switching to
                            // Custom never blanks the view.
                            startDate: value?.startDate || active?.start,
                            endDate: value?.endDate || active?.last_day,
                        },
                )}
                className={`px-3 py-2 text-[13.5px] font-semibold rounded-seg border transition-colors min-w-0 max-w-full truncate ${
                    preset === 'custom'
                        ? 'bg-white/[0.1] text-white border-pai'
                        : 'text-pai-meta border-pai-subtle hover:text-white hover:bg-white/[0.06]'
                }`}
            >
                {t('energyRangeCustom', 'Custom')}
            </button>

            {preset === 'custom' && (
                <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                    <input
                        type="date"
                        data-testid="energy-range-start"
                        aria-label={t('energyRangeFrom', 'From')}
                        value={value?.startDate || ''}
                        onChange={(e) => setBound('startDate', e.target.value)}
                        className="pai-input px-2.5 py-1.5 text-[13px] tnum min-w-0 [color-scheme:dark]"
                    />
                    <span className="text-[13px] text-pai-faint">–</span>
                    <input
                        type="date"
                        data-testid="energy-range-end"
                        aria-label={t('energyRangeTo', 'To')}
                        value={value?.endDate || ''}
                        onChange={(e) => setBound('endDate', e.target.value)}
                        className="pai-input px-2.5 py-1.5 text-[13px] tnum min-w-0 [color-scheme:dark]"
                    />
                </div>
            )}
        </div>
    );
};

export default EnergyRangePicker;
